import { useEffect, useRef } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { gsap, reducedMotion } from '@/lib/anim'

/** Scroll-triggered reveal: rise + fade, once. */
export function Reveal({
  children,
  delay = 0,
  y = 32,
  duration = 1.1,
  className = '',
  style,
  as: Tag = 'div',
}: {
  children: ReactNode
  delay?: number
  y?: number
  duration?: number
  className?: string
  style?: CSSProperties
  as?: 'div' | 'section' | 'span' | 'h2' | 'h3' | 'p'
}) {
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || reducedMotion) return
    gsap.set(el, { opacity: 0, y })
    const tween = gsap.to(el, {
      opacity: 1,
      y: 0,
      duration,
      delay,
      ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
    })
    return () => {
      tween.scrollTrigger?.kill()
      tween.kill()
    }
  }, [delay, y, duration])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const C: any = Tag
  return (
    <C ref={ref} className={className} style={style}>
      {children}
    </C>
  )
}

/** Image that settles from a slow zoom as it scrolls into view. */
export function ImageFrame({
  src,
  alt,
  ratio = '3/2',
  className = '',
  caption,
}: {
  src: string
  alt: string
  ratio?: string
  className?: string
  caption?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || reducedMotion) return
    const img = el.querySelector('img')
    gsap.set(el, { opacity: 0 })
    if (img) gsap.set(img, { scale: 1.18 })
    const tl = gsap.timeline({
      scrollTrigger: { trigger: el, start: 'top 85%', once: true },
    })
    tl.to(el, { opacity: 1, duration: 0.9, ease: 'power2.out' })
    if (img) tl.to(img, { scale: 1, duration: 1.6, ease: 'power3.out' }, '<')
    return () => {
      tl.scrollTrigger?.kill()
      tl.kill()
    }
  }, [])
  return (
    <figure className={className}>
      <div ref={ref} className="overflow-hidden" style={{ aspectRatio: ratio }}>
        <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" />
      </div>
      {caption && (
        <figcaption className="mt-3 font-mono-data text-[11px] uppercase tracking-[0.18em]" style={{ color: '#96968A' }}>
          {caption}
        </figcaption>
      )}
    </figure>
  )
}

/** Animated counter for stat numbers. */
export function CountUp({
  value,
  suffix = '',
  className = '',
}: {
  value: number
  suffix?: string
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (reducedMotion) {
      el.textContent = `${value}${suffix}`
      return
    }
    const obj = { v: 0 }
    const tween = gsap.to(obj, {
      v: value,
      duration: 1.8,
      ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 90%', once: true },
      onUpdate: () => {
        el.textContent = `${Math.round(obj.v)}${suffix}`
      },
    })
    return () => {
      tween.scrollTrigger?.kill()
      tween.kill()
    }
  }, [value, suffix])
  return (
    <span ref={ref} className={className}>
      {reducedMotion ? `${value}${suffix}` : `0${suffix}`}
    </span>
  )
}
